import axios from "axios";
import { logError } from "./logger.js";

export const api = axios.create({
  baseURL: "/api",
  withCredentials: true,
});

// Sessions opened from a personal-email onboarding link carry scope
// "onboarding". They have no refresh cookie, so the token is kept in
// sessionStorage (tab-scoped, 2h lifetime) to survive a page reload.
const ONBOARDING_SESSION_KEY = "hrms.onboardingSession";

export function isOnboardingSessionToken(token: string | null): boolean {
  if (!token) return false;
  try {
    const payload = token.split(".")[1]!.replace(/-/g, "+").replace(/_/g, "/");
    return (JSON.parse(atob(payload)) as { scope?: string }).scope === "onboarding";
  } catch {
    return false;
  }
}

function readOnboardingSession(): string | null {
  try { return sessionStorage.getItem(ONBOARDING_SESSION_KEY); } catch { return null; }
}

let _accessToken: string | null = readOnboardingSession();
const tokenListeners = new Set<(token: string | null) => void>();

export function setAccessToken(token: string | null) {
  _accessToken = token;
  try {
    if (token && isOnboardingSessionToken(token)) sessionStorage.setItem(ONBOARDING_SESSION_KEY, token);
    else sessionStorage.removeItem(ONBOARDING_SESSION_KEY);
  } catch { /* storage unavailable: the session just won't survive a reload */ }
  tokenListeners.forEach((l) => l(token));
}

export function getAccessToken(): string | null {
  return _accessToken;
}

export function subscribeAccessToken(listener: (token: string | null) => void): () => void {
  tokenListeners.add(listener);
  return () => tokenListeners.delete(listener);
}

let refreshPromise: Promise<string | null> | null = null;

export function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = api
      .post<{ accessToken: string }>("/auth/refresh")
      .then(({ data }) => {
        setAccessToken(data.accessToken);
        return data.accessToken;
      })
      .catch(() => {
        setAccessToken(null);
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

api.interceptors.request.use((config) => {
  if (_accessToken) {
    config.headers["Authorization"] = `Bearer ${_accessToken}`;
  }
  return config;
});

export function errMsg(e: unknown): string | undefined {
  const data = (e as { response?: { data?: { error?: string; issues?: Array<{ message?: string; path?: (string | number)[] }> } } })?.response?.data;
  if (data?.issues?.length) {
    return data.issues.map(i => i.path?.length ? `${i.path.join(".")}: ${i.message}` : i.message).filter(Boolean).join("; ");
  }
  return data?.error;
}

export function errStatus(e: unknown): number | undefined {
  return (e as { response?: { status?: number } })?.response?.status;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config as typeof error.config & { _retry?: boolean };
    const isRefreshCall = original?.url === "/auth/refresh";
    // An onboarding-link session can't be refreshed (and must never pick up
    // some other user's refresh cookie in this browser): send them back to
    // the link page, which explains how to get back in.
    if (error.response?.status === 401 && isOnboardingSessionToken(_accessToken)) {
      setAccessToken(null);
      window.location.href = "/onboarding/start?ended=1";
      return Promise.reject(error);
    }
    if (error.response?.status === 401 && !original._retry && !isRefreshCall) {
      original._retry = true;
      const token = await refreshAccessToken();
      if (token) {
        original.headers["Authorization"] = `Bearer ${token}`;
        return api(original);
      }
      window.location.href = "/login";
    }
    // Log all non-401 errors automatically; enrich error.message from server body first
    // so the logged message is the real server error, not the generic axios message.
    if (error.response?.status !== 401) {
      const serverMsg = (error.response?.data as { error?: string } | undefined)?.error;
      if (serverMsg) error.message = serverMsg;
      logError("axios.response", error, {
        status: error.response?.status as number | undefined,
        url:    error.config?.url as string | undefined,
        method: error.config?.method as string | undefined,
      });
    }
    return Promise.reject(error);
  }
);
